import { AnimatePresence, motion } from 'motion/react'
import {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { getItemDef } from '../game/items'
import {
  BuildWorkstationsSheet,
  getPendingInitialBuildSummary,
} from '../components/BuildWorkstationsSheet'
import { HideoutTierBanner } from '../components/HideoutTierBanner'
import { ResourceStrip } from '../components/ResourceStrip'
import { WorkstationCard } from '../components/WorkstationCard'
import { useI18n } from '../i18n/useI18n'
import {
  buildHideoutFlavorContext,
  hideoutFlavorTimeBucket,
  pickHideoutFlavorMessage,
} from '../game/hideoutFlavor'
import {
  getInitialBuildGate,
  HIDEOUT_TIER_META,
  meetsHideoutTierMinimum,
} from '../game/hideoutProgression'
import {
  garageIsOverCapacity,
  maxGarageSlots,
  scrapYieldForVehicleDef,
  totalGarageSlotsUsed,
} from '../game/garageOps'
import { expeditionParticipantIds } from '../game/locations'
import { ingredientOwned } from '../game/recipes'
import {
  getVehicleDef,
  getVehicleEffectiveStatsForInstance,
  VEHICLE_REPAIR_SCRAP_COST,
  VEHICLE_REPAIR_DURABILITY_PER_SCRAP,
} from '../game/vehicles'
import { WORKSTATION_LIST } from '../game/workstations'
import { getPortraitSrc } from '../game/portraits'
import type { Expedition, Survivor, VehicleInstance, WorkstationId } from '../game/types'
import { useGameStore } from '../store/useGameStore'

const WorkstationDetailScreen = lazy(() =>
  import('./WorkstationDetailScreen').then((m) => ({
    default: m.WorkstationDetailScreen,
  })),
)

export function HideoutScreen({ forceReset }: { forceReset?: number }) {
  const { t, pick } = useI18n()
  const inventory = useGameStore((s) => s.inventory)
  const survivors = useGameStore((s) => s.survivors)
  const progress = useGameStore((s) => s.progress)
  const locationExploration = useGameStore((s) => s.locationExploration)
  const quests = useGameStore((s) => s.quests)
  const hideoutTierId = useGameStore((s) => s.hideoutTierId)
  const workstations = useGameStore((s) => s.workstations)
  const garageVehicles = useGameStore((s) => s.garageVehicles)
  const expeditions = useGameStore((s) => s.expeditions)
  const buildWorkstation = useGameStore((s) => s.buildWorkstation)
  const repairVehicle = useGameStore((s) => s.repairVehicle)
  const scrapVehicle = useGameStore((s) => s.scrapVehicle)
  const [detailId, setDetailId] = useState<WorkstationId | null>(null)
  const navDirectionRef = useRef<'forward' | 'back'>('forward')
  const [buildSheetOpen, setBuildSheetOpen] = useState(false)
  const [hiddenUntilReveal, setHiddenUntilReveal] = useState<WorkstationId | null>(null)
  const [newlyBuiltId, setNewlyBuiltId] = useState<WorkstationId | null>(null)
  const [flavorTimeBucket, setFlavorTimeBucket] = useState(() =>
    hideoutFlavorTimeBucket(Date.now()),
  )

  useEffect(() => {
    if (!forceReset) return
    setDetailId(null)
    setBuildSheetOpen(false)
  }, [forceReset])

  useEffect(() => {
    const sync = () => {
      const next = hideoutFlavorTimeBucket(Date.now())
      setFlavorTimeBucket((prev) => (prev !== next ? next : prev))
    }
    sync()
    const id = window.setInterval(sync, 60_000)
    return () => window.clearInterval(id)
  }, [])

  const builtStationList = useMemo(
    () => WORKSTATION_LIST.filter((s) => workstations[s.id]?.built && s.id !== hiddenUntilReveal),
    [workstations, hiddenUntilReveal],
  )
  const workstationGridCols = builtStationList.length >= 4 ? 3 : 2
  const buildCardColSpanClass =
    workstationGridCols === 3
      ? builtStationList.length % 3 === 0
        ? 'col-span-3'
        : builtStationList.length % 3 === 1
          ? 'col-span-2'
          : ''
      : builtStationList.length % 2 === 0
        ? 'col-span-2'
        : ''
  const vehicleQuestStarted = quests.vehicleQuest.status !== 'inactive'

  const pendingBuildSummary = useMemo(
    () => getPendingInitialBuildSummary(workstations, inventory, hideoutTierId, vehicleQuestStarted),
    [workstations, inventory, hideoutTierId, vehicleQuestStarted],
  )

  const showHideoutStats = workstations.base_station?.assignedSurvivorId != null

  const kitchenBuilt = workstations.kitchen?.built === true
  const canAffordKitchenBuild = useMemo(() => {
    if (kitchenBuilt) return false
    const gate = getInitialBuildGate('kitchen')
    if (!gate) return false
    if (!meetsHideoutTierMinimum(hideoutTierId, gate.minHideoutTier)) return false
    return gate.costs.every((ing) => ingredientOwned(inventory, ing) >= ing.quantity)
  }, [kitchenBuilt, hideoutTierId, inventory])

  const showBuildWorkstationsButton =
    pendingBuildSummary.pendingCount > 0 && (kitchenBuilt || canAffordKitchenBuild)

  const idleCount = survivors.filter((s) => s.status === 'Idle').length
  const onMissionCount = survivors.filter((s) => s.status === 'OnMission').length
  const lowestNeed = survivors.reduce((min, s) => {
    const n = Math.min(s.needs.hunger, s.needs.thirst, s.needs.energy)
    return Math.min(min, n)
  }, 100)

  const tierName = pick(HIDEOUT_TIER_META[hideoutTierId].name)

  const hideoutFlavorLine = useMemo(() => {
    const ctx = buildHideoutFlavorContext({
      inventory,
      survivors,
      progress,
      locationExploration,
      quests,
      garageVehicles,
    })
    return pickHideoutFlavorMessage(ctx, survivors, flavorTimeBucket)
  }, [
    flavorTimeBucket,
    inventory,
    survivors,
    progress,
    locationExploration,
    quests,
    garageVehicles,
  ])

  const slideVariants = {
    enterForward: { opacity: 0, x: 24 },
    enterBack: { opacity: 0, x: -24 },
    center: { opacity: 1, x: 0 },
    exitForward: { opacity: 0, x: -24 },
    exitBack: { opacity: 0, x: 24 },
  }

  return (
    <AnimatePresence mode="wait" initial={false}>
      {detailId ? (
        <motion.div
          key={`ws-${detailId}`}
          initial={slideVariants.enterForward}
          animate={slideVariants.center}
          exit={slideVariants.exitForward}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
        >
          <Suspense
            fallback={
              <div className="flex min-h-[40vh] items-center justify-center text-xs text-stone-500">
                {t('app_loading')}
              </div>
            }
          >
            <WorkstationDetailScreen
              workstationId={detailId}
              onBack={() => {
                navDirectionRef.current = 'back'
                setDetailId(null)
              }}
            />
          </Suspense>
        </motion.div>
      ) : (
        <motion.div
          key="hideout-grid"
          initial={navDirectionRef.current === 'back' ? slideVariants.enterBack : false}
          animate={slideVariants.center}
          exit={slideVariants.exitBack}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
        >
    <div className="space-y-4">
      <div data-tutorial-id="resource-strip">
        <ResourceStrip inventory={inventory} survivorCount={survivors.length} />
      </div>

      <HideoutTierBanner
        tierId={hideoutTierId}
        label={t('hideout_tier_label')}
        tierName={tierName}
      />

      {showHideoutStats ? (
        <section className="grid grid-cols-3 gap-2">
          <StatTile label={t('hideout_stat_idle')} value={idleCount} accent="text-emerald-400" />
          <StatTile label={t('hideout_stat_missions')} value={onMissionCount} accent="text-amber-400" />
          <StatTile
            label={t('hideout_stat_lowest')}
            value={Math.round(lowestNeed)}
            suffix="%"
            accent={
              lowestNeed < 20
                ? 'text-red-400'
                : lowestNeed < 40
                  ? 'text-amber-400'
                  : 'text-stone-200'
            }
          />
        </section>
      ) : null}

      <section>
        <SectionHeader title={t('hideout_workstations')} />
        <div
          data-tutorial-id="workstation-grid"
          className={`grid gap-2 ${workstationGridCols === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}
        >
          {builtStationList.map((station) => (
            <WorkstationCard
              key={station.id}
              station={station}
              level={Math.max(1, workstations[station.id]?.level ?? 1)}
              inventory={inventory}
              metric={
                station.id === 'garage'
                  ? { glyph: '🚗', value: garageVehicles.length }
                  : station.id === 'lodging'
                    ? {
                        glyph: 'Zz',
                        value: workstations.lodging?.lodgingOccupantIds.length ?? 0,
                      }
                    : undefined
              }
              built
              isNew={station.id === newlyBuiltId}
              onOpen={(id) => {
                navDirectionRef.current = 'forward'
                setNewlyBuiltId(null)
                setDetailId(id)
              }}
            />
          ))}
          {showBuildWorkstationsButton ? (
            <button
              type="button"
              onClick={() => setBuildSheetOpen(true)}
              aria-label={
                pendingBuildSummary.hasAffordableBuild
                  ? `${t('hideout_build_card_title')}. ${t('hideout_build_card_available', { count: pendingBuildSummary.pendingCount })}. ${t('hideout_build_ready_badge', { count: pendingBuildSummary.affordableCount })}`
                  : `${t('hideout_build_card_title')}. ${t('hideout_build_card_available', { count: pendingBuildSummary.pendingCount })}`
              }
              title={
                pendingBuildSummary.hasAffordableBuild
                  ? t('hideout_build_ready_hint')
                  : undefined
              }
              className={`flex h-full w-full min-h-0 flex-col ${buildCardColSpanClass} gap-1 rounded-lg border border-dashed border-amber-900/55 bg-stone-950 p-3 text-left transition-colors hover:border-amber-700/70 hover:bg-stone-800`}
            >
              <div className="flex w-full items-start justify-between gap-2">
                <span className="text-2xl leading-none select-none" aria-hidden>
                  🔨
                </span>
                {pendingBuildSummary.hasAffordableBuild ? (
                  <span className="shrink-0 rounded-full border border-amber-600/80 bg-amber-950/90 px-2 py-1 font-mono text-[10px] font-medium tabular-nums text-amber-500">
                    {t('hideout_build_ready_badge', {
                      count: pendingBuildSummary.affordableCount,
                    })}
                  </span>
                ) : null}
              </div>
              <div className="min-w-0 flex-1 pt-4">
               
                <div className="text-sm font-semibold leading-tight text-amber-500">
                  {t('hideout_build_card_title')}
                </div>
                <div className="mt-1 text-[10px] italic leading-snug text-stone-500">
                  {t('hideout_build_card_available', {
                    count: pendingBuildSummary.pendingCount,
                  })}
                </div>
              </div>
            </button>
          ) : null}
        </div>
      </section>

      <BuildWorkstationsSheet
        open={buildSheetOpen}
        onClose={() => setBuildSheetOpen(false)}
        inventory={inventory}
        hideoutTierId={hideoutTierId}
        workstations={workstations}
        vehicleQuestStarted={vehicleQuestStarted}
        onBuild={(stationId) => {
          buildWorkstation(stationId)
          setBuildSheetOpen(false)
          setHiddenUntilReveal(stationId)
          setNewlyBuiltId(null)
          setTimeout(() => {
            setHiddenUntilReveal(null)
            setNewlyBuiltId(stationId)
          }, 420)
        }}
      />

      {workstations.garage?.built && (
        <section>
          <SectionHeader title={t('garage_section_title')} />
          <GarageSection
            garageLevel={
              workstations.garage?.built === true
                ? Math.max(1, workstations.garage.level)
                : 0
            }
            vehicles={garageVehicles}
            expeditions={expeditions}
            survivors={survivors}
            scrapCount={inventory.reduce(
              (sum, s) =>
                s.defId === 'scrap-rusty' ? sum + s.quantity : sum,
              0,
            )}
            onRepair={repairVehicle}
            onScrap={scrapVehicle}
          />
        </section>
      )}

      <p className="px-1 pt-1 text-[11px] italic leading-relaxed text-stone-500">
        {t(hideoutFlavorLine.key, hideoutFlavorLine.params)}
      </p>
    </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function GarageSection({
  garageLevel,
  vehicles,
  expeditions,
  survivors,
  scrapCount,
  onRepair,
  onScrap,
}: {
  garageLevel: number
  vehicles: VehicleInstance[]
  expeditions: Expedition[]
  survivors: Survivor[]
  scrapCount: number
  onRepair: (id: string) => void
  onScrap: (id: string) => void
}) {
  const { t, pick } = useI18n()
  const [scrapConfirmId, setScrapConfirmId] = useState<string | null>(null)
  const slotUsed = totalGarageSlotsUsed(vehicles)
  const slotCap = maxGarageSlots(garageLevel)
  const overCapacity = garageIsOverCapacity(garageLevel, vehicles)

  if (vehicles.length === 0) {
    return (
      <>
        <p className="mb-2 text-[10px] uppercase tracking-wider text-stone-500">
          {t('garage_slots_line', { used: 0, max: slotCap })}
        </p>
        <p className="rounded-md border border-stone-800 bg-stone-800/40 p-3 text-xs text-stone-500 italic">
          {t('garage_empty')}
        </p>
      </>
    )
  }

  let scrapConfirmModal: ReactNode = null
  if (scrapConfirmId) {
    const sv = vehicles.find((x) => x.id === scrapConfirmId)
    const sd = sv ? getVehicleDef(sv.defId) : undefined
    if (sv && sd) {
      const scrapPreview = scrapYieldForVehicleDef(sv.defId)
        .map((s) => {
          const itemDef = getItemDef(s.defId)
          const label = itemDef ? pick(itemDef.name) : s.defId
          return `${s.quantity}× ${label}`
        })
        .join(', ')
      scrapConfirmModal = (
        <div
          className="fixed inset-0 z-60 flex items-center justify-center bg-black/70 p-4"
          role="presentation"
          onClick={() => setScrapConfirmId(null)}
        >
          <div
            className="w-full max-w-sm rounded-lg border border-amber-800/70 bg-stone-950 p-4 text-center shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300">
              {t('garage_scrap_popup_title')}
            </div>
            <div className="mt-2 text-base font-semibold text-stone-100">
              {pick(sd.name)}
            </div>
            <div className="mt-3 text-left">
              <p className="text-[9px] text-stone-500">{t('garage_scrap_yield_label')}</p>
              <p className="mt-0.5 wrap-break-word text-[10px] italic leading-snug text-stone-400">
                {scrapPreview}
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                onScrap(sv.id)
                setScrapConfirmId(null)
              }}
              className="mt-4 w-full rounded-md border border-red-800/80 bg-red-950/40 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-red-100 hover:bg-red-950/70"
            >
              {t('garage_scrap_confirm')}
            </button>
            <button
              type="button"
              onClick={() => setScrapConfirmId(null)}
              className="mt-2 w-full rounded-md border border-stone-700 bg-transparent px-3 py-2 text-xs font-semibold uppercase tracking-wider text-stone-400 hover:bg-stone-800"
            >
              {t('garage_scrap_cancel')}
            </button>
          </div>
        </div>
      )
    }
  }

  return (
    <div className="space-y-2">
      <div className="rounded-md border border-stone-800 bg-stone-950/40 px-2 py-1.5 text-[10px] text-stone-400">
        <span className="font-mono tabular-nums text-stone-300">
          {t('garage_slots_line', { used: slotUsed, max: slotCap })}
        </span>
        <p className="mt-0.5 text-[9px] italic text-stone-600">{t('garage_slots_legend')}</p>
        {overCapacity ? (
          <p className="mt-1 text-[10px] text-amber-400">{t('garage_over_capacity_hint')}</p>
        ) : null}
      </div>
      {vehicles.map((v) => {
        const def = getVehicleDef(v.defId)
        if (!def) return null
        const onTripExp = expeditions.find(
          (exp) =>
            exp.status === 'active' &&
            (exp.vehicleInstanceId === v.id ||
              (exp.vehicleInstanceIds?.includes(v.id) ?? false)),
        )
        const onTripTeam = onTripExp
          ? expeditionParticipantIds(onTripExp)
              .map((id) => survivors.find((s) => s.id === id))
              .filter((s): s is Survivor => !!s)
          : []
        const onTripLeader = onTripTeam[0]
        const onTripExtra = Math.max(0, onTripTeam.length - 1)
        const canRepair = !onTripExp && v.durability < 100 && scrapCount >= VEHICLE_REPAIR_SCRAP_COST
        const durabilityColor =
          v.durability > 60
            ? 'bg-emerald-600'
            : v.durability > 25
              ? 'bg-amber-500'
              : 'bg-red-600'
        return (
          <div
            key={v.id}
            className="rounded-md border border-stone-800 bg-stone-800/70 p-3"
          >
            <div className="grid grid-cols-4 gap-3 items-start">
              <div className="col-span-3 min-w-0 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="text-xl shrink-0 leading-none">{def.glyph}</span>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold leading-tight text-stone-100">
                      {pick(def.name)}
                    </div>
                    {(() => {
                      const eff = getVehicleEffectiveStatsForInstance(v)
                      return (
                        <div className="mt-0.5 text-[10px] text-emerald-400">
                          +{Math.round((eff.lootMultiplier - 1) * 100)}% loot
                        </div>
                      )
                    })()}
                    {!onTripExp && v.durability < 100 ? (
                      <button
                        type="button"
                        disabled={!canRepair}
                        onClick={() => onRepair(v.id)}
                        className={`mt-1.5 rounded border px-2 py-1 text-[10px] uppercase tracking-wider transition-colors ${
                          canRepair
                            ? 'border-amber-700/70 bg-amber-950/20 text-amber-300 hover:border-amber-600 hover:bg-amber-950/50 hover:text-amber-200'
                            : 'cursor-not-allowed border-stone-800 text-stone-600'
                        }`}
                      >
                        {t('garage_repair_btn', {
                          scrap: VEHICLE_REPAIR_SCRAP_COST,
                          pct: VEHICLE_REPAIR_DURABILITY_PER_SCRAP,
                        })}
                      </button>
                    ) : null}
                  </div>
                </div>
                <div>
                  <div className="mb-1 flex items-center justify-between gap-2 text-[10px] text-stone-500">
                    <span className="uppercase tracking-wider">{t('garage_durability')}</span>
                    <span className="shrink-0 font-mono tabular-nums text-stone-300">
                      {v.durability}%
                    </span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-stone-800 ring-1 ring-inset ring-stone-700">
                    <div
                      className={`h-full transition-[width] duration-500 ${durabilityColor}`}
                      style={{ width: `${v.durability}%` }}
                    />
                  </div>
                </div>
              </div>

              <div className="col-span-1 min-w-0 flex flex-col gap-2">
                {onTripLeader ? (
                  <div className="flex justify-end">
                    <div className="flex max-w-full items-center gap-1.5 rounded border border-amber-800/50 bg-amber-950/30 px-2 py-1">
                      <div className="h-5 w-5 shrink-0 overflow-hidden rounded border border-stone-700 bg-stone-800">
                        {getPortraitSrc(onTripLeader.portrait) ? (
                          <img
                            src={getPortraitSrc(onTripLeader.portrait)!}
                            alt={onTripLeader.name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="grid h-full w-full place-items-center font-mono text-[8px] font-bold text-stone-400">
                            {onTripLeader.name.slice(0, 2).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <span className="min-w-0 truncate text-[10px] font-semibold text-amber-300">
                        {onTripExtra > 0
                          ? t('exp_card_away_team', {
                              leader: onTripLeader.name,
                              count: onTripExtra,
                            })
                          : onTripLeader.name}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-end">
                    <button
                      type="button"
                      onClick={() => setScrapConfirmId(v.id)}
                      className="rounded border border-stone-700 px-2 py-1 text-[10px] uppercase tracking-wider text-stone-400 hover:border-amber-800/50 hover:text-amber-200"
                    >
                      {t('garage_scrap_btn')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      })}
      {scrapConfirmModal}
    </div>
  )
}

function SectionHeader({ title }: { title: string }) {
  return (
    <h2 className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">
      {title}
    </h2>
  )
}

function StatTile({
  label,
  value,
  suffix,
  accent = 'text-stone-200',
}: {
  label: string
  value: number
  suffix?: string
  accent?: string
}) {
  return (
    <div className="rounded-lg border border-stone-800 bg-stone-800/70 p-2.5 text-center">
      <div className={`font-mono text-xl font-bold ${accent}`}>
        {value}
        {suffix}
      </div>
      <div className="text-[9px] uppercase tracking-wider text-stone-500">{label}</div>
    </div>
  )
}
